// ========== وضعیت‌ها و کمک‌تابع‌های مشترک پیش‌فاکتور/سفارش ==========
// مقادیر دقیقاً همان enum های اسکیمای سرور هستند:
//   order_status: proforma | sales_order | in_production | produced |
//                 in_warehouse | out_of_warehouse | loaded | delivered
//   item_status : proforma | sales_order | in_production | produced | in_warehouse |
//                 out_of_warehouse | loaded | delivered

export type OrderStatus =
  | "proforma"
  | "sales_order"
  | "in_production"
  | "produced"
  | "in_warehouse"
  | "out_of_warehouse"
  | "loaded"
  | "delivered";

export type ItemStatus =
  | "proforma"
  | "sales_order"
  | "in_production"
  | "produced"
  | "in_warehouse"
  | "out_of_warehouse"
  | "loaded"
  | "delivered";

export const ORDER_STATUS_LABELS: Record<string, string> = {
  proforma: "پیش‌فاکتور",
  sales_order: "سفارش فروش / آماده تولید",
  in_production: "در حال تولید",
  produced: "تولید شده",
  in_warehouse: "در انبار محصولات",
  out_of_warehouse: "خارج‌شده از انبار",
  loaded: "بارگیری‌شده",
  delivered: "تحویل‌شده به مشتری",
};

export const ORDER_STATUS_CLASSES: Record<string, string> = {
  proforma: "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400",
  sales_order: "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400",
  in_production: "bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400",
  produced: "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400",
  in_warehouse: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-400",
  out_of_warehouse: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-400",
  loaded: "bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-400",
  delivered: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-400",
};

export const ITEM_STATUS_LABELS: Record<string, string> = {
  proforma: ORDER_STATUS_LABELS.proforma,
  sales_order: ORDER_STATUS_LABELS.sales_order,
  in_production: ORDER_STATUS_LABELS.in_production,
  produced: ORDER_STATUS_LABELS.produced,
  in_warehouse: ORDER_STATUS_LABELS.in_warehouse,
  out_of_warehouse: ORDER_STATUS_LABELS.out_of_warehouse,
  loaded: ORDER_STATUS_LABELS.loaded,
  delivered: ORDER_STATUS_LABELS.delivered,
};

export const ITEM_STATUS_CLASSES: Record<string, string> = {
  proforma: ORDER_STATUS_CLASSES.proforma,
  sales_order: ORDER_STATUS_CLASSES.sales_order,
  in_production: ORDER_STATUS_CLASSES.in_production,
  produced: ORDER_STATUS_CLASSES.produced,
  in_warehouse: ORDER_STATUS_CLASSES.in_warehouse,
  out_of_warehouse: ORDER_STATUS_CLASSES.out_of_warehouse,
  loaded: ORDER_STATUS_CLASSES.loaded,
  delivered: ORDER_STATUS_CLASSES.delivered,
};

const NEUTRAL_BADGE = "bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-400";

export const orderStatusLabel = (status?: string | null) =>
  (status && ORDER_STATUS_LABELS[status]) || status || "-";

export const orderStatusClass = (status?: string | null) =>
  (status && ORDER_STATUS_CLASSES[status]) || NEUTRAL_BADGE;

export const itemStatusLabel = (status?: string | null) =>
  (status && ITEM_STATUS_LABELS[status]) || status || "-";

export const itemStatusClass = (status?: string | null) =>
  (status && ITEM_STATUS_CLASSES[status]) || NEUTRAL_BADGE;

// طبق مستند: در صورت خالی بودن goods_description از «کالای شماره {line_number}» استفاده شود.
export const getItemLabel = (
  item: { goods_description?: string | null; goods_full_description?: string | null; line_number?: number | null },
  fallbackIndex?: number
) =>
  item?.goods_full_description?.trim() ||
  item?.goods_description?.trim() ||
  `کالای شماره ${item?.line_number ?? (fallbackIndex !== undefined ? fallbackIndex + 1 : "-")}`;

// مبالغ Decimal به‌صورت رشته برمی‌گردند. برای تشخیص «صفر بودن» نباید به عدد شناور
// تبدیل شوند، چون مقادیر بزرگ دقت را از دست می‌دهند؛ کافی است دنبال رقم غیرصفر بگردیم.
export const isPositiveDecimalString = (raw?: string | number | null): boolean => {
  if (raw === undefined || raw === null) return false;
  const cleaned = String(raw).replace(/[+,\s]/g, "").replace(/^-/, "");
  if (!cleaned) return false;
  return /[1-9]/.test(cleaned);
};

interface SplitDecimal {
  negative: boolean;
  integer: string;
  fraction: string;
}

// رشته‌ی Decimal را به بخش صحیح و اعشاری نرمال‌شده می‌شکند. برگشت null یعنی مقدار
// عددی معتبری نبود و نباید بر اساس آن قضاوت کرد.
const splitDecimalString = (raw?: string | number | null): SplitDecimal | null => {
  if (raw === undefined || raw === null) return null;
  const cleaned = String(raw).replace(/[+,\s]/g, "");
  if (!cleaned) return null;

  const negative = cleaned.charAt(0) === "-";
  const body = negative ? cleaned.slice(1) : cleaned;
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(body)) return null;

  const dot = body.indexOf(".");
  const integer = (dot === -1 ? body : body.slice(0, dot)).replace(/^0+/, "") || "0";
  const fraction = (dot === -1 ? "" : body.slice(dot + 1)).replace(/0+$/, "");

  return { negative, integer, fraction };
};

const padRight = (value: string, length: number) => {
  let padded = value;
  while (padded.length < length) padded += "0";
  return padded;
};

const compareMagnitude = (a: SplitDecimal, b: SplitDecimal): number => {
  if (a.integer.length !== b.integer.length) {
    return a.integer.length < b.integer.length ? -1 : 1;
  }
  if (a.integer !== b.integer) return a.integer < b.integer ? -1 : 1;

  const width = Math.max(a.fraction.length, b.fraction.length);
  const af = padRight(a.fraction, width);
  const bf = padRight(b.fraction, width);
  if (af === bf) return 0;
  return af < bf ? -1 : 1;
};

/**
 * مقایسه‌ی دو مبلغ Decimal بدون تبدیل به عدد شناور.
 * خروجی: -1 اگر a < b، صفر اگر برابر باشند، 1 اگر a > b و null اگر یکی از مقادیر معتبر نباشد.
 */
export const compareDecimalStrings = (
  a?: string | number | null,
  b?: string | number | null
): number | null => {
  const left = splitDecimalString(a);
  const right = splitDecimalString(b);
  if (!left || !right) return null;

  const leftZero = left.integer === "0" && left.fraction === "";
  const rightZero = right.integer === "0" && right.fraction === "";
  const leftNeg = left.negative && !leftZero;
  const rightNeg = right.negative && !rightZero;

  if (leftNeg !== rightNeg) return leftNeg ? -1 : 1;
  const magnitude = compareMagnitude(left, right);
  return leftNeg ? -magnitude : magnitude;
};

/**
 * وضعیت تسویه‌ی نمایشی سفارش را مطابق قرارداد API فقط از settlement_status می‌خواند.
 * تصمیم نهایی درباره مجازبودن ثبت یا تأیید پرداخت با سرور است.
 */
export const isSettled = (row?: {
  settlement_status?: boolean | null;
} | null): boolean => {
  return row?.settlement_status === true;
};
